import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { su01Of } from "../../domain/su01";
import { InfoTip } from "../components/Erklaerung";
import { Badge, Card, KlasseBadge, KpiCard } from "../components/ui";
import { useApp } from "../../store/AppStore";
import { hasMultiClient, multiClientStats } from "../../domain/analysis";
import { resolveIdentity } from "../../domain/identity";
import { assignedLandscapes } from "../../data/landscapes";
import { CLASS_LABEL, CLASS_ORDER, formatFue, formatNum } from "../../domain/rules";
import type { IdentityKey, LicenseClass, Person, PersonType, TraceState } from "../../domain/types";

// Trace-Status einer Person = Trace ihres ersten entfernbaren Treibers.
type TraceFilter = TraceState | "keiner";
function personTrace(p: Person): TraceFilter {
  const d = p.drivers.find((x) => x.removable);
  return d ? d.trace : "keiner";
}
const TRACE_LABEL: Record<TraceFilter, string> = {
  "nie-genutzt": "nie genutzt",
  genutzt: "genutzt",
  "kein-trace": "kein Trace",
  keiner: "kein Hebel",
};
const TRACE_COLOR: Record<TraceFilter, string> = {
  "nie-genutzt": "var(--color-amp-green)",
  genutzt: "var(--color-amp-amber)",
  "kein-trace": "var(--color-amp-gray)",
  keiner: "var(--border-strong)",
};

const KEY_LABEL: Record<string, string> = {
  username: "Benutzername",
  personalnummer: "Personalnummer",
  email: "E-Mail",
};
const IDENTITY_KEYS: IdentityKey[] = ["username", "personalnummer", "email"];

const TYPE_LABEL: Record<PersonType, string> = {
  lager: "Lager",
  buero: "Büro",
  it: "IT",
  entwickler: "Entwickler",
  extern: "Extern",
  technisch: "Technisch",
};
const TYPE_FILTERS: PersonType[] = ["lager", "buero", "it", "entwickler", "extern"];

const MAX_COLLISIONS = 20;

// Virtualisierung: nur das sichtbare Fenster wird gerendert (voller 60k-Bestand).
const ROW_H = 44;
const VIEWPORT_H = 560;
const OVERSCAN = 8;
const GRID_COLS = "minmax(110px,1.3fr) 0.9fr 1.2fr 1.2fr 1.3fr 0.6fr 0.7fr";

export function Personen() {
  const { ds, identityKey, setIdentityKey, dataSource, ratio } = useApp();
  const accById = useMemo(() => new Map(ds.accounts.map((a) => [a.id, a])), [ds]);
  const [typeFilter, setTypeFilter] = useState<PersonType | "alle">("alle");
  const [classFilter, setClassFilter] = useState<LicenseClass | "alle">("alle");
  const [landscapeFilter, setLandscapeFilter] = useState<string>("alle");
  const [traceFilter, setTraceFilter] = useState<TraceFilter | "alle">("alle");
  // Mehrere Mandanten im selben System — der Alltagsfall bei 110 Mandanten (GP7).
  const [multiClient, setMultiClient] = useState(false);
  const mc = useMemo(() => multiClientStats(ds), [ds]);
  const [search, setSearch] = useState("");
  const [scrollTop, setScrollTop] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Aktueller Schlüssel — Kollisionen und Konsolidierungsbild.
  const current = useMemo(() => resolveIdentity(ds.accounts, ds.persons, identityKey, ratio), [ds, identityKey, ratio]);

  // Wirkung je Schlüssel (Personenzahl + FUE) für die Wirkungsanzeige — mit der Verhältnistabelle,
  // damit die FUE-Differenz zur Personalnummer dieselbe ist wie im Cockpit.
  const rows = useMemo(
    () => IDENTITY_KEYS.map((k) => ({ k, res: resolveIdentity(ds.accounts, ds.persons, k, ratio) })),
    [ds, ratio],
  );
  const byPn = rows.find((r) => r.k === "personalnummer")!.res;
  const diffToPn = current.recognizedPersons - byPn.recognizedPersons;
  const diffFueToPn = current.fueTotal - byPn.fueTotal;

  // AP6.3: Such-Index je Person — Pseudonym, Einheit, Systeme (sid) UND Rollen (einmal gebaut).
  const searchIndex = useMemo(() => {
    // Suchindex je Person: Pseudonym, Name, E-Mail, Einheit, Abteilung, Standort, Benutzernamen der Konten
    const accById = new Map(ds.accounts.map((a) => [a.id, a]));
    const compName = new Map(ds.compositeRoles.map((c) => [c.id, c.name]));
    const singleName = new Map(ds.singleRoles.map((r) => [r.id, r.name]));
    const idx = new Map<string, string>();
    for (const p of ds.persons) {
      const parts = [p.id, p.name ?? "", p.email ?? "", p.unit, p.department ?? "", p.location ?? ""];
      for (const aid of p.accountIds) {
        const a = accById.get(aid); if (!a) continue;
        parts.push(a.username, a.sid); // System und Rolle sind suchbar (AP6.3)
        for (const c of a.compositeRoleIds) parts.push(c, compName.get(c) ?? "");
        for (const r of a.singleRoleIds) parts.push(r, singleName.get(r) ?? "");
      }
      idx.set(p.id, parts.join(" ").toLowerCase());
    }
    return idx;
  }, [ds]);

  // Volltextsuche + Filter über den GANZEN Bestand.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase(); // sucht Pseudonym, Name, E-Mail, Einheit, Abteilung
    return ds.persons.filter((p) => {
      if (typeFilter !== "alle" && p.type !== typeFilter) return false;
      if (classFilter !== "alle" && p.licenseClass !== classFilter) return false;
      if (landscapeFilter !== "alle" && !p.landscapeIds.includes(landscapeFilter)) return false;
      if (traceFilter !== "alle" && personTrace(p) !== traceFilter) return false;
      if (multiClient && !hasMultiClient(p)) return false;
      if (q && !(searchIndex.get(p.id) ?? "").includes(q)) return false;
      return true;
    });
  }, [ds, searchIndex, typeFilter, classFilter, landscapeFilter, traceFilter, search, multiClient]);

  // Beim Filterwechsel an den Anfang springen.
  useEffect(() => {
    setScrollTop(0);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [typeFilter, classFilter, landscapeFilter, traceFilter, search, multiClient]);

  // Sichtbares Fenster berechnen.
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const endIndex = Math.min(filtered.length, Math.ceil((scrollTop + VIEWPORT_H) / ROW_H) + OVERSCAN);
  const visible = filtered.slice(startIndex, endIndex);
  const padTop = startIndex * ROW_H;
  const padBottom = Math.max(0, (filtered.length - endIndex) * ROW_H);

  // Konfiguration der Filter-Dropdowns.
  const SELECTS: { key: string; value: string; set: (v: string) => void; options: { value: string; label: string }[] }[] = [
    { key: "typ", value: typeFilter, set: (v) => setTypeFilter(v as PersonType | "alle"),
      options: [{ value: "alle", label: "Typ: alle" }, ...TYPE_FILTERS.map((t) => ({ value: t, label: TYPE_LABEL[t] }))] },
    { key: "klasse", value: classFilter, set: (v) => setClassFilter(v as LicenseClass | "alle"),
      options: [{ value: "alle", label: "Klasse: alle" }, ...[...CLASS_ORDER].reverse().map((c) => ({ value: c, label: CLASS_LABEL[c] }))] },
    { key: "landschaft", value: landscapeFilter, set: (v) => setLandscapeFilter(v),
      options: [{ value: "alle", label: "Landschaft: alle" }, ...assignedLandscapes.map((l) => ({ value: l.id, label: l.name }))] },
    { key: "trace", value: traceFilter, set: (v) => setTraceFilter(v as TraceFilter | "alle"),
      options: [{ value: "alle", label: "Trace: alle" }, ...(["nie-genutzt", "genutzt", "kein-trace", "keiner"] as TraceFilter[]).map((t) => ({ value: t, label: TRACE_LABEL[t] }))] },
  ];

  return (
    <div>
      <PageHeader route="/personen" />

      {/* Konsolidierungsbild */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard label="Konten (roh)" value={formatNum(ds.accounts.length)} sub="Benutzerstamm über alle Landschaften" />
        <KpiCard label={`Personen (${KEY_LABEL[identityKey] ?? identityKey})`} value={formatNum(current.recognizedPersons)} sub="konsolidiert nach aktivem Schlüssel" tone="brand" big />
        <KpiCard label="Technische User" value={formatNum(ds.technicalCount)} sub="ausgewiesen, aber nicht gezählt" />
      </div>

      {/* R1 innerhalb eines Systems: viele Mandanten, eine Person. */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm" style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}>
        <span className="text-dim inline-flex flex-wrap items-center gap-x-3 gap-y-1">
          <span><span className="text-strong font-semibold">{formatNum(mc.personsTotal)}</span> Personen mit Konten in mehreren Mandanten eines Systems</span>
          {Object.values(mc.bySid).filter((x) => x.persons >= 50).sort((a, b) => b.persons - a.persons).map((x) => (
            <span key={x.sid} className="text-faint text-xs"><span className="font-mono">{x.sid}</span> <span className="tnum">{formatNum(x.persons)}</span> · bis {x.maxClients} Mandanten</span>
          ))}
          <InfoTip>Konten derselben Person in mehreren Mandanten eines Systems zählen als eine Person mit einem FUE (R1 gilt auch innerhalb eines Systems). Die Klasse ist das Maximum über alle Mandanten.</InfoTip>
        </span>
        <label className="inline-flex items-center gap-2 text-xs">
          <input type="checkbox" checked={multiClient} onChange={(e) => setMultiClient(e.target.checked)} />
          nur diese Personen zeigen
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-3 rounded-lg border-soft px-4 py-3 text-sm" style={{ borderWidth: 1, backgroundColor: "var(--surface-sunken)" }}>
        <span className="tnum text-dim">{formatNum(ds.accounts.length)} Konten</span>
        <span className="text-faint">→</span>
        <span className="text-dim">Schlüssel {KEY_LABEL[identityKey] ?? identityKey}</span>
        <span className="text-faint">→</span>
        <span className="tnum text-strong font-semibold">{formatNum(current.recognizedPersons)} Personen</span>
        <InfoTip>Fehlt der Schlüssel oder ist er mehrdeutig, wird nicht zusammengeführt; die Person zählt dann einzeln.</InfoTip>
      </div>

      {/* Wirkungsanzeige (§5.5) */}
      <Card
        title="Wirkung des Identitätsschlüssels"
        subtitle="Umschalten rechnet live neu"
        className="mt-6"
      >
        <div className="flex flex-wrap gap-2">
          {rows.map(({ k, res }) => {
            const active = k === identityKey;
            return (
              <button
                key={k}
                onClick={() => setIdentityKey(k)}
                className="rounded-lg px-4 py-3 text-left"
                style={{
                  borderWidth: 1,
                  borderColor: active ? "var(--color-brand-600)" : "var(--border-soft)",
                  backgroundColor: active ? "var(--surface-sunken)" : "transparent",
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-strong text-sm font-semibold">{KEY_LABEL[k]}</span>
                  {active && <Badge tone="brand">aktiv</Badge>}
                </div>
                <div className="tnum text-normal mt-1 text-lg font-semibold">{formatNum(res.recognizedPersons)}</div>
                <div className="tnum text-dim text-xs">Personen · {formatFue(res.fueTotal)}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg p-3" style={{ backgroundColor: "var(--surface-sunken)" }}>
            <div className="text-dim text-[13px]">Differenz aktiv gegenüber Personalnummer</div>
            <div className="tnum mt-1 text-2xl font-semibold" style={{ color: diffToPn > 0 ? "var(--color-amp-red)" : "var(--text-strong)" }}>
              {diffToPn > 0 ? "+" : ""}{formatNum(diffToPn)} Personen
            </div>
            <div className="text-faint mt-1 text-xs">
              Personalnummer führt vollständig zusammen ({formatNum(byPn.recognizedPersons)}), der aktive Schlüssel erkennt {formatNum(current.recognizedPersons)}.
            </div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: "var(--surface-sunken)" }}>
            <div className="text-dim inline-flex items-center gap-1 text-[13px]">
              Konten derselben Person, die der Schlüssel nicht zusammenführt
              <InfoTip size={12}>Differenz des aktiven Schlüssels gegenüber der Personalnummer: so viele Einheiten mehr erkennt er, und so viel FUE zählt der Bedarf dadurch höher. Unter der Personalnummer ist der Wert 0.</InfoTip>
            </div>
            <div className="tnum mt-1 text-2xl font-semibold" style={{ color: diffToPn > 0 ? "var(--color-amp-red)" : "var(--color-amp-green)" }}>
              {formatNum(Math.max(0, diffToPn))} Personen
            </div>
            <div className="tnum text-faint mt-1 text-xs">
              {diffFueToPn >= 0 ? "+" : "−"}{formatFue(Math.abs(diffFueToPn))} gegenüber Personalnummer
            </div>
          </div>
        </div>
      </Card>

      {/* Kollisionsliste */}
      <Card
        title="Kollisionen — mehrdeutig, NICHT zusammengeführt"
        subtitle={current.collisions.length === 0 ? "keine" : `${formatNum(current.collisions.length)} gefunden · erste ${Math.min(MAX_COLLISIONS, current.collisions.length)} gezeigt`}
        className="mt-6"
      >
        {current.collisions.length === 0 ? (
          <div className="text-dim text-sm">Keine Kollisionen unter dem Schlüssel „{KEY_LABEL[identityKey] ?? identityKey}".</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-soft">
                  {["Schlüssel", "Widerspruch", "Konten"].map((h, i) => (
                    <th key={h} className={`text-faint py-2 text-xs font-semibold tracking-wide uppercase ${i === 2 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {current.collisions.slice(0, MAX_COLLISIONS).map((c, i) => (
                  <tr key={`${c.key}-${i}`} className="border-b border-soft last:border-0">
                    <td className="text-strong py-2.5 font-mono text-xs">{c.key}</td>
                    <td className="text-dim py-2.5">{c.conflict}</td>
                    <td className="tnum text-normal py-2.5 text-right">{formatNum(c.accounts.length)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Personenliste — virtualisiert über den vollen Bestand */}
      <Card
        title="Personen"
        subtitle={dataSource === "seed" ? "Vollständiger Bestand · Namen fiktiv (Seed)" : "Vollständiger Bestand · Pseudonyme"}
        right={<Badge tone="outline">{dataSource === "seed" ? "fiktive Namen (Seed)" : "Pseudonymisiert"}</Badge>}
        className="mt-6"
      >
        {/* Such- und Filterleiste */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={15} className="text-faint pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={dataSource === "seed" ? "Name, Benutzername, E-Mail, System oder Rolle…" : "Pseudonym, Einheit, System oder Rolle…"}
              className="bg-card rounded-lg py-1.5 pr-3 pl-8 text-sm"
              style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-strong)", minWidth: 200 }}
            />
          </div>
          {SELECTS.map((sel) => (
            <select
              key={sel.key}
              value={sel.value}
              onChange={(e) => sel.set(e.target.value)}
              className="bg-card rounded-lg px-2.5 py-1.5 text-sm"
              style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }}
            >
              {sel.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ))}
        </div>

        <div className="text-dim mb-2 text-sm">
          <span className="tnum text-strong font-semibold">{formatNum(filtered.length)}</span> Treffer von {formatNum(ds.persons.length)} · vollständige Liste, nur das Sichtbare wird gerendert
        </div>

        {/* Kopfzeile */}
        <div className="text-faint border-b border-soft px-2 py-2 text-xs font-semibold tracking-wide uppercase" style={{ display: "grid", gridTemplateColumns: GRID_COLS, alignItems: "center" }}>
          <span>Pseudonym</span>
          <span>Typ</span>
          <span>Funktion (SU01)</span>
          <span>Lizenzklasse</span>
          <span>Trace</span>
          <span className="text-right">Konten</span>
          <span className="text-right">Landsch.</span>
        </div>

        {/* Virtualisierter Scrollbereich */}
        {filtered.length === 0 ? (
          <div className="text-dim px-2 py-8 text-center text-sm">Keine Person passt zu den Filtern.</div>
        ) : (
          <div
            ref={scrollRef}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            style={{ height: VIEWPORT_H, overflowY: "auto" }}
          >
            <div style={{ height: padTop }} />
            {visible.map((p) => {
              const tr = personTrace(p);
              return (
                <div
                  key={p.id}
                  className="border-b border-soft px-2 text-sm"
                  style={{ display: "grid", gridTemplateColumns: GRID_COLS, alignItems: "center", height: ROW_H }}
                >
                  <Link to={`/berechtigungen?person=${p.id}`} className={p.name ? "text-sm font-medium hover:underline" : "font-mono text-xs font-medium hover:underline"} style={{ color: "var(--color-brand-600)" }}>
                    {p.name ?? p.id}
                    {p.name && <span className="text-faint ml-1.5 font-mono text-[11px]">{p.id.replace(/^P-/, "")}</span>}
                  </Link>
                  <span><Badge tone="neutral">{TYPE_LABEL[p.type]}</Badge></span>
                  <span className="text-dim truncate text-xs" title={su01Of(p, accById).abteilung}>{su01Of(p, accById).funktion ?? "—"}</span>
                  <span><KlasseBadge cls={p.licenseClass} /></span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: TRACE_COLOR[tr] }} aria-hidden />
                    <span className="text-dim text-xs">{TRACE_LABEL[tr]}</span>
                  </span>
                  <span className="tnum text-dim text-right">{formatNum(p.accountIds.length)}</span>
                  <span className="tnum text-dim text-right">{formatNum(p.landscapeIds.length)}</span>
                </div>
              );
            })}
            <div style={{ height: padBottom }} />
          </div>
        )}
        <div className="text-faint mt-3 text-xs">
          Virtualisierte Liste: der volle Bestand von {formatNum(ds.persons.length)} Personen ist durchscrollbar, gerendert wird nur das sichtbare Fenster. Klick auf ein Pseudonym öffnet den Drilldown.
        </div>
      </Card>
    </div>
  );
}
